import { useCallback, useEffect, useRef, useState } from 'react';
import usePreferences, { PreferenceKey } from 'hooks/usePreferences';
import { OrientationOptions } from './useOperationPdf';

type ExportFn = () => Promise<void>;

/**
 * Applies the user's saved PDF orientation preference to a `useOperationPdf`
 * export. The PDF document is rendered with the `orientation` returned here,
 * so an export requested while the orientation still has to change is held
 * until the re-render has rebuilt the document, then run once.
 *
 * `runExport` is invoked after that re-render, so it must reach the export
 * function built for the new orientation (read it through a ref) rather than
 * the one captured when the download started.
 */
const useSavedPdfOrientation = (userId: string) => {
  const { findPreferenceByKey } = usePreferences();
  const [orientation, setOrientation] = useState<OrientationOptions>(
    'portrait'
  );
  const pendingExportRef = useRef<ExportFn | null>(null);

  useEffect(() => {
    const pendingExport = pendingExportRef.current;
    if (!pendingExport) return;
    pendingExportRef.current = null;
    pendingExport();
  }, [orientation]);

  const cancelPendingExport = useCallback(() => {
    pendingExportRef.current = null;
  }, []);

  const exportWithSavedOrientation = useCallback(
    async (runExport: ExportFn, isCancelled: () => boolean = () => false) => {
      const savedPreference = await findPreferenceByKey(
        PreferenceKey.OPERATION_PDF_ORIENTATION,
        { userId }
      );
      if (isCancelled()) return;

      const isLandscape = savedPreference?.preferenceValue?.isLandscape;
      if (isLandscape !== undefined) {
        const savedOrientation: OrientationOptions = isLandscape
          ? 'landscape'
          : 'portrait';
        if (savedOrientation !== orientation) {
          pendingExportRef.current = runExport;
          setOrientation(savedOrientation);
          return;
        }
      }

      await runExport();
    },
    [findPreferenceByKey, userId, orientation]
  );

  return { orientation, exportWithSavedOrientation, cancelPendingExport };
};

export default useSavedPdfOrientation;
